import { useEffect, useState } from "react";
import { KeyRound, Store, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { ChangePinForm } from "@/components/ChangePinForm";

const STORE_KEY = "verify_store_id";

function useStorePinLogin(onUnlock) {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(() => localStorage.getItem(STORE_KEY) || "");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);
  const [pendingChange, setPendingChange] = useState(null);

  useEffect(() => {
    api.listStores().then((s) => {
      setStores(s);
      setStoreId((cur) => cur || s[0]?.id || "");
    });
  }, []);

  const login = async () => {
    if (!storeId) return toast.error("Seleziona la sede");
    if (!pin) return toast.error("Inserisci il PIN");
    setBusy(true);
    try {
      const r = await api.storeLogin(storeId, pin, remember);
      localStorage.setItem(STORE_KEY, storeId);
      if (r.must_change_pin) {
        setPendingChange({ store: r.store, currentPin: pin });
        return;
      }
      if (remember) toast.success("Punto vendita ricordato su questo dispositivo per 30 giorni");
      onUnlock(r.store);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN non corretto");
      setPin("");
    } finally {
      setBusy(false);
    }
  };
  const finishChange = () => {
    const store = pendingChange.store;
    setPendingChange(null);
    setPin("");
    onUnlock(store);
  };
  return { stores, storeId, setStoreId, pin, setPin, busy, remember, setRemember, login, pendingChange, finishChange };
}

function GateHeader() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-12 w-12 rounded-xl bg-amber-500/15 grid place-items-center">
        <LockKeyhole className="h-6 w-6 text-amber-400" />
      </div>
      <div>
        <h2 className="font-display font-bold text-lg">Accesso riservato al personale</h2>
        <p className="text-sm text-muted-foreground">Seleziona la sede e inserisci il PIN per verificare e ritirare premi.</p>
      </div>
    </div>
  );
}

function PinForm({ s }) {
  return (
    <>
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> Punto vendita</Label>
        <select data-testid="pin-store-select" value={s.storeId} onChange={(e) => s.setStoreId(e.target.value)}
          className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
          {s.stores.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> PIN</Label>
        <Input data-testid="pin-input" type="password" required inputMode="numeric" value={s.pin} onChange={(e) => s.setPin(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && s.login()} placeholder="••••••" className="text-center text-xl tracking-[0.5em] font-mono-code" />
      </div>
      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="pin-remember-label">
        <input type="checkbox" checked={s.remember} onChange={(e) => s.setRemember(e.target.checked)} className="h-4 w-4 accent-blue-500" data-testid="pin-remember-checkbox" />
        Ricorda questo punto vendita su questo dispositivo per 30 giorni
      </label>
      <Button data-testid="pin-submit-button" onClick={s.login} disabled={s.busy} className="w-full h-12 gap-2">
        <LockKeyhole className="h-4 w-4" /> {s.busy ? "Verifica…" : "Sblocca"}
      </Button>
    </>
  );
}

export default function StorePinGate({ onUnlock, embedded = false }) {
  const s = useStorePinLogin(onUnlock);
  let body;
  if (s.pendingChange) body = <ChangePinForm storeName={s.pendingChange.store.name} currentPin={s.pendingChange.currentPin} onDone={s.finishChange} />;
  else if (s.stores.length === 0) body = <p className="text-sm text-amber-400" data-testid="no-stores-warning">Nessuna sede configurata: crea i punti vendita dal pannello "Punti Vendita".</p>;
  else body = <PinForm s={s} />;
  if (embedded) return <div className="space-y-4" data-testid="store-pin-gate">{body}</div>;
  return (
    <Card className="p-6 bg-card border-border space-y-4" data-testid="store-pin-gate">
      <GateHeader />
      {body}
    </Card>
  );
}
